import React, { useState, useMemo } from 'react';
import { validateIBAN } from '../../utils/validation';

export default function InternationalTransfer({ accounts, onTransfer }) {
  const [fromAccount, setFromAccount] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [bankName, setBankName] = useState('');
  const [swiftCode, setSwiftCode] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [iban, setIban] = useState('');
  const [country, setCountry] = useState('');
  const [countrySearch, setCountrySearch] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState('USD');
  const [currencySearch, setCurrencySearch] = useState('');
  const [reference, setReference] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showCotMessage, setShowCotMessage] = useState(false);
  const [transferData, setTransferData] = useState(null);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);
  const [showCurrencyDropdown, setShowCurrencyDropdown] = useState(false);

  // Common currencies for international transfers
  const currencies = [
    { code: 'USD', symbol: '$', name: 'US Dollar' },
    { code: 'EUR', symbol: '€', name: 'Euro' },
    { code: 'GBP', symbol: '£', name: 'British Pound' },
    { code: 'JPY', symbol: '¥', name: 'Japanese Yen' },
    { code: 'AUD', symbol: 'A$', name: 'Australian Dollar' },
    { code: 'CAD', symbol: 'C$', name: 'Canadian Dollar' },
    { code: 'CHF', symbol: 'Fr', name: 'Swiss Franc' },
    { code: 'CNY', symbol: '¥', name: 'Chinese Yuan' },
    { code: 'HKD', symbol: 'HK$', name: 'Hong Kong Dollar' },
    { code: 'NZD', symbol: 'NZ$', name: 'New Zealand Dollar' },
    { code: 'SEK', symbol: 'kr', name: 'Swedish Krona' },
    { code: 'KRW', symbol: '₩', name: 'South Korean Won' },
    { code: 'SGD', symbol: 'S$', name: 'Singapore Dollar' },
    { code: 'NOK', symbol: 'kr', name: 'Norwegian Krone' },
    { code: 'MXN', symbol: '$', name: 'Mexican Peso' },
    { code: 'INR', symbol: '₹', name: 'Indian Rupee' },
    { code: 'RUB', symbol: '₽', name: 'Russian Ruble' },
    { code: 'ZAR', symbol: 'R', name: 'South African Rand' },
    { code: 'TRY', symbol: '₺', name: 'Turkish Lira' },
    { code: 'BRL', symbol: 'R$', name: 'Brazilian Real' },
    { code: 'TWD', symbol: 'NT$', name: 'Taiwan Dollar' },
    { code: 'DKK', symbol: 'kr', name: 'Danish Krone' },
    { code: 'PLN', symbol: 'zł', name: 'Polish Złoty' },
    { code: 'THB', symbol: '฿', name: 'Thai Baht' },
    { code: 'IDR', symbol: 'Rp', name: 'Indonesian Rupiah' },
    { code: 'AED', symbol: 'د.إ', name: 'UAE Dirham' },
    { code: 'SAR', symbol: '﷼', name: 'Saudi Riyal' },
    { code: 'ARS', symbol: '$', name: 'Argentine Peso' },
    { code: 'ILS', symbol: '₪', name: 'Israeli Shekel' },
    { code: 'PHP', symbol: '₱', name: 'Philippine Peso' }
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Comprehensive list of countries
  const countries = [
    { code: 'AF', name: 'Afghanistan' },
    { code: 'AL', name: 'Albania' },
    { code: 'DZ', name: 'Algeria' },
    { code: 'AD', name: 'Andorra' },
    { code: 'AO', name: 'Angola' },
    { code: 'AG', name: 'Antigua and Barbuda' },
    { code: 'AR', name: 'Argentina' },
    { code: 'AM', name: 'Armenia' },
    { code: 'AU', name: 'Australia' },
    { code: 'AT', name: 'Austria' },
    { code: 'AZ', name: 'Azerbaijan' },
    { code: 'BS', name: 'Bahamas' },
    { code: 'BH', name: 'Bahrain' },
    { code: 'BD', name: 'Bangladesh' },
    { code: 'BB', name: 'Barbados' },
    { code: 'BY', name: 'Belarus' },
    { code: 'BE', name: 'Belgium' },
    { code: 'BZ', name: 'Belize' },
    { code: 'BJ', name: 'Benin' },
    { code: 'BT', name: 'Bhutan' },
    { code: 'BO', name: 'Bolivia' },
    { code: 'BA', name: 'Bosnia and Herzegovina' },
    { code: 'BW', name: 'Botswana' },
    { code: 'BR', name: 'Brazil' },
    { code: 'BN', name: 'Brunei' },
    { code: 'BG', name: 'Bulgaria' },
    { code: 'BF', name: 'Burkina Faso' },
    { code: 'BI', name: 'Burundi' },
    { code: 'KH', name: 'Cambodia' },
    { code: 'CM', name: 'Cameroon' },
    { code: 'CA', name: 'Canada' },
    { code: 'CV', name: 'Cape Verde' },
    { code: 'CF', name: 'Central African Republic' },
    { code: 'TD', name: 'Chad' },
    { code: 'CL', name: 'Chile' },
    { code: 'CN', name: 'China' },
    { code: 'CO', name: 'Colombia' },
    { code: 'KM', name: 'Comoros' },
    { code: 'CG', name: 'Congo' },
    { code: 'CR', name: 'Costa Rica' },
    { code: 'HR', name: 'Croatia' },
    { code: 'CU', name: 'Cuba' },
    { code: 'CY', name: 'Cyprus' },
    { code: 'CZ', name: 'Czech Republic' },
    { code: 'DK', name: 'Denmark' },
    { code: 'DJ', name: 'Djibouti' },
    { code: 'DM', name: 'Dominica' },
    { code: 'DO', name: 'Dominican Republic' },
    { code: 'EC', name: 'Ecuador' },
    { code: 'EG', name: 'Egypt' },
    { code: 'SV', name: 'El Salvador' },
    { code: 'GQ', name: 'Equatorial Guinea' },
    { code: 'ER', name: 'Eritrea' },
    { code: 'EE', name: 'Estonia' },
    { code: 'ET', name: 'Ethiopia' },
    { code: 'FJ', name: 'Fiji' },
    { code: 'FI', name: 'Finland' },
    { code: 'FR', name: 'France' },
    { code: 'GA', name: 'Gabon' },
    { code: 'GM', name: 'Gambia' },
    { code: 'GE', name: 'Georgia' },
    { code: 'DE', name: 'Germany' },
    { code: 'GH', name: 'Ghana' },
    { code: 'GR', name: 'Greece' },
    { code: 'GD', name: 'Grenada' },
    { code: 'GT', name: 'Guatemala' },
    { code: 'GN', name: 'Guinea' },
    { code: 'GW', name: 'Guinea-Bissau' },
    { code: 'GY', name: 'Guyana' },
    { code: 'HT', name: 'Haiti' },
    { code: 'HN', name: 'Honduras' },
    { code: 'HU', name: 'Hungary' },
    { code: 'IS', name: 'Iceland' },
    { code: 'IN', name: 'India' },
    { code: 'ID', name: 'Indonesia' },
    { code: 'IR', name: 'Iran' },
    { code: 'IQ', name: 'Iraq' },
    { code: 'IE', name: 'Ireland' },
    { code: 'IL', name: 'Israel' },
    { code: 'IT', name: 'Italy' },
    { code: 'JM', name: 'Jamaica' },
    { code: 'JP', name: 'Japan' },
    { code: 'JO', name: 'Jordan' },
    { code: 'KZ', name: 'Kazakhstan' },
    { code: 'KE', name: 'Kenya' },
    { code: 'KI', name: 'Kiribati' },
    { code: 'KP', name: 'North Korea' },
    { code: 'KR', name: 'South Korea' },
    { code: 'KW', name: 'Kuwait' },
    { code: 'KG', name: 'Kyrgyzstan' },
    { code: 'LA', name: 'Laos' },
    { code: 'LV', name: 'Latvia' },
    { code: 'LB', name: 'Lebanon' },
    { code: 'LS', name: 'Lesotho' },
    { code: 'LR', name: 'Liberia' },
    { code: 'LY', name: 'Libya' },
    { code: 'LI', name: 'Liechtenstein' },
    { code: 'LT', name: 'Lithuania' },
    { code: 'LU', name: 'Luxembourg' },
    { code: 'MK', name: 'North Macedonia' },
    { code: 'MG', name: 'Madagascar' },
    { code: 'MW', name: 'Malawi' },
    { code: 'MY', name: 'Malaysia' },
    { code: 'MV', name: 'Maldives' },
    { code: 'ML', name: 'Mali' },
    { code: 'MT', name: 'Malta' },
    { code: 'MH', name: 'Marshall Islands' },
    { code: 'MR', name: 'Mauritania' },
    { code: 'MU', name: 'Mauritius' },
    { code: 'MX', name: 'Mexico' },
    { code: 'FM', name: 'Micronesia' },
    { code: 'MD', name: 'Moldova' },
    { code: 'MC', name: 'Monaco' },
    { code: 'MN', name: 'Mongolia' },
    { code: 'ME', name: 'Montenegro' },
    { code: 'MA', name: 'Morocco' },
    { code: 'MZ', name: 'Mozambique' },
    { code: 'MM', name: 'Myanmar' },
    { code: 'NA', name: 'Namibia' },
    { code: 'NR', name: 'Nauru' },
    { code: 'NP', name: 'Nepal' },
    { code: 'NL', name: 'Netherlands' },
    { code: 'NZ', name: 'New Zealand' },
    { code: 'NI', name: 'Nicaragua' },
    { code: 'NE', name: 'Niger' },
    { code: 'NG', name: 'Nigeria' },
    { code: 'NO', name: 'Norway' },
    { code: 'OM', name: 'Oman' },
    { code: 'PK', name: 'Pakistan' },
    { code: 'PW', name: 'Palau' },
    { code: 'PA', name: 'Panama' },
    { code: 'PG', name: 'Papua New Guinea' },
    { code: 'PY', name: 'Paraguay' },
    { code: 'PE', name: 'Peru' },
    { code: 'PH', name: 'Philippines' },
    { code: 'PL', name: 'Poland' },
    { code: 'PT', name: 'Portugal' },
    { code: 'QA', name: 'Qatar' },
    { code: 'RO', name: 'Romania' },
    { code: 'RU', name: 'Russia' },
    { code: 'RW', name: 'Rwanda' },
    { code: 'KN', name: 'Saint Kitts and Nevis' },
    { code: 'LC', name: 'Saint Lucia' },
    { code: 'VC', name: 'Saint Vincent and the Grenadines' },
    { code: 'WS', name: 'Samoa' },
    { code: 'SM', name: 'San Marino' },
    { code: 'ST', name: 'Sao Tome and Principe' },
    { code: 'SA', name: 'Saudi Arabia' },
    { code: 'SN', name: 'Senegal' },
    { code: 'RS', name: 'Serbia' },
    { code: 'SC', name: 'Seychelles' },
    { code: 'SL', name: 'Sierra Leone' },
    { code: 'SG', name: 'Singapore' },
    { code: 'SK', name: 'Slovakia' },
    { code: 'SI', name: 'Slovenia' },
    { code: 'SB', name: 'Solomon Islands' },
    { code: 'SO', name: 'Somalia' },
    { code: 'ZA', name: 'South Africa' },
    { code: 'SS', name: 'South Sudan' },
    { code: 'ES', name: 'Spain' },
    { code: 'LK', name: 'Sri Lanka' },
    { code: 'SD', name: 'Sudan' },
    { code: 'SR', name: 'Suriname' },
    { code: 'SE', name: 'Sweden' },
    { code: 'CH', name: 'Switzerland' },
    { code: 'SY', name: 'Syria' },
    { code: 'TW', name: 'Taiwan' },
    { code: 'TJ', name: 'Tajikistan' },
    { code: 'TZ', name: 'Tanzania' },
    { code: 'TH', name: 'Thailand' },
    { code: 'TL', name: 'Timor-Leste' },
    { code: 'TG', name: 'Togo' },
    { code: 'TO', name: 'Tonga' },
    { code: 'TT', name: 'Trinidad and Tobago' },
    { code: 'TN', name: 'Tunisia' },
    { code: 'TR', name: 'Turkey' },
    { code: 'TM', name: 'Turkmenistan' },
    { code: 'TV', name: 'Tuvalu' },
    { code: 'UG', name: 'Uganda' },
    { code: 'UA', name: 'Ukraine' },
    { code: 'AE', name: 'United Arab Emirates' },
    { code: 'GB', name: 'United Kingdom' },
    { code: 'US', name: 'United States' },
    { code: 'UY', name: 'Uruguay' },
    { code: 'UZ', name: 'Uzbekistan' },
    { code: 'VU', name: 'Vanuatu' },
    { code: 'VA', name: 'Vatican City' },
    { code: 'VE', name: 'Venezuela' },
    { code: 'VN', name: 'Vietnam' },
    { code: 'YE', name: 'Yemen' },
    { code: 'ZM', name: 'Zambia' },
    { code: 'ZW', name: 'Zimbabwe' }
  ].sort((a, b) => a.name.localeCompare(b.name));

  // Filter countries based on search
  const filteredCountries = useMemo(() => {
    return countries.filter(c => 
      c.name.toLowerCase().includes(countrySearch.toLowerCase()) ||
      c.code.toLowerCase().includes(countrySearch.toLowerCase())
    );
  }, [countrySearch]);

  // Filter currencies based on search
  const filteredCurrencies = useMemo(() => {
    return currencies.filter(c => 
      c.name.toLowerCase().includes(currencySearch.toLowerCase()) ||
      c.code.toLowerCase().includes(currencySearch.toLowerCase())
    );
  }, [currencySearch]);

  // Format currency
  const formatCurrency = (value, currencyCode = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currencyCode
    }).format(value);
  };

  const validateForm = () => {
    if (!fromAccount || !recipientName || !bankName || !swiftCode || !accountNumber || !country || !amount || !reference) {
      setError('Please fill in all fields');
      return false;
    }

    if (swiftCode.length < 8 || swiftCode.length > 11) {
      setError('Invalid SWIFT/BIC code');
      return false;
    }

    if (accountNumber.length < 8 || accountNumber.length > 34) {
      setError('Invalid account number');
      return false;
    }

    const transferAmount = parseFloat(amount);
    if (isNaN(transferAmount) || transferAmount <= 0) {
      setError('Please enter a valid amount');
      return false;
    }

    const sourceAccount = accounts.find(acc => acc.type === fromAccount);
    if (transferAmount > sourceAccount.available) {
      setError('Insufficient funds');
      return false;
    }

    return true;
  };

  const handleInitialSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    const data = {
      fromAccount,
      recipientName,
      bankName,
      swiftCode,
      accountNumber,
      country,
      amount: parseFloat(amount),
      currency,
      reference,
      transferType: 'international'
    };

    setTransferData(data);
    setShowCotMessage(true);
  };

  const handleConfirmTransfer = async () => {
    setLoading(true);
    try {
      await onTransfer(transferData);
      
      // Reset form
      setFromAccount('');
      setRecipientName('');
      setBankName('');
      setSwiftCode('');
      setAccountNumber('');
      setIban('');
      setCountry('');
      setAmount('');
      setCurrency('USD');
      setReference('');
      setShowConfirmation(false);
      setTransferData(null);
    } catch (error) {
      setError('Transfer failed. Please try again.');
    }
    setLoading(false);
  };

  // Replace the country select with a searchable dropdown
  const renderCountryDropdown = () => (
    <div className="relative">
      <div
        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
          focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
          hover:bg-white transition-colors duration-200
          disabled:bg-gray-100 disabled:cursor-not-allowed
          text-sm cursor-pointer flex justify-between items-center"
        onClick={() => setShowCountryDropdown(!showCountryDropdown)}
      >
        <span className="text-gray-900">{country || 'Select country'}</span>
        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
      {showCountryDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200">
          <input
            type="text"
            value={countrySearch}
            onChange={(e) => setCountrySearch(e.target.value)}
              className="w-full px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            placeholder="Search countries..."
            onClick={(e) => e.stopPropagation()}
          />
          </div>
          <div className="py-1">
          {filteredCountries.map((c) => (
            <div
              key={c.code}
                className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-900"
              onClick={() => {
                setCountry(c.name);
                setShowCountryDropdown(false);
              }}
            >
              {c.name}
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );

  // Replace the currency select with a searchable dropdown
  const renderCurrencyDropdown = () => (
    <div className="relative">
      <div
        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
          focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
          hover:bg-white transition-colors duration-200
          disabled:bg-gray-100 disabled:cursor-not-allowed
          text-sm cursor-pointer flex justify-between items-center"
        onClick={() => setShowCurrencyDropdown(!showCurrencyDropdown)}
      >
        <span className="text-gray-900">
          {currency ? `${currency} - ${currencies.find(c => c.code === currency)?.name}` : 'Select currency'}
        </span>
        <svg className="h-5 w-5 text-gray-400" viewBox="0 0 20 20" fill="currentColor">
          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
      </div>
      {showCurrencyDropdown && (
        <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
          <div className="sticky top-0 bg-white border-b border-gray-200">
          <input
            type="text"
            value={currencySearch}
            onChange={(e) => setCurrencySearch(e.target.value)}
              className="w-full px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 text-sm"
            placeholder="Search currencies..."
            onClick={(e) => e.stopPropagation()}
          />
          </div>
          <div className="py-1">
          {filteredCurrencies.map((c) => (
            <div
              key={c.code}
                className="px-4 py-2 hover:bg-gray-50 cursor-pointer text-sm text-gray-900"
              onClick={() => {
                setCurrency(c.code);
                setShowCurrencyDropdown(false);
              }}
            >
                <span className="inline-block w-12">{c.code}</span>
                <span className="text-gray-600">- {c.name}</span>
            </div>
          ))}
          </div>
        </div>
      )}
    </div>
  );

  // Add click outside handler to close dropdowns
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showCountryDropdown || showCurrencyDropdown) {
        if (!event.target.closest('.relative')) {
          setShowCountryDropdown(false);
          setShowCurrencyDropdown(false);
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showCountryDropdown, showCurrencyDropdown]);

  if (showCotMessage) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-xl p-6 space-y-6">
          <div className="flex items-center justify-center">
            <div className="bg-yellow-100 rounded-full p-3">
              <svg className="h-8 w-8 text-yellow-600" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
            </div>
          </div>
          
          <h3 className="text-xl font-semibold text-center text-gray-900">
            Important Notice
          </h3>
          
          <p className="text-gray-600 text-center">
            International transfers may be subject to First Equityal fees and processing time.
            Please review the transfer details carefully before proceeding.
          </p>

          <div className="bg-gray-50 p-4 rounded-xl space-y-3">
            <h4 className="text-sm font-medium text-gray-700">Transfer Summary</h4>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">To:</span>
                <span className="text-sm font-medium text-gray-900">{recipientName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Bank:</span>
                <span className="text-sm font-medium text-gray-900">{bankName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Country:</span>
                <span className="text-sm font-medium text-gray-900">{country}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-sm text-gray-600">Amount:</span>
                <span className="text-sm font-medium text-gray-900">
                  {formatCurrency(parseFloat(amount), currency)}
                </span>
              </div>
            </div>
          </div>

          <div className="pt-4 flex space-x-4">
            <button
              type="button"
              onClick={() => setShowCotMessage(false)}
              className="flex-1 py-3 px-4 border border-gray-200 rounded-xl shadow-sm text-gray-700 
                bg-white hover:bg-gray-50 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
                disabled:bg-gray-100 disabled:cursor-not-allowed
                transition-colors duration-200"
              disabled={loading}
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleConfirmTransfer}
              className="flex-1 flex justify-center py-3 px-4 border border-transparent rounded-xl 
                shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
                disabled:bg-gray-300 disabled:cursor-not-allowed
                transition-colors duration-200"
              disabled={loading}
            >
              {loading ? (
                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : (
                'Confirm Transfer'
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (showConfirmation) {
    return (
      <div className="space-y-6">
        <div className="bg-yellow-50 p-4 rounded-xl">
          <h3 className="text-lg font-medium text-yellow-800 mb-2">
            Please Confirm Your International Transfer
          </h3>
          <div className="space-y-2 text-sm text-yellow-700">
            <p>Please verify all details carefully. International transfers cannot be reversed.</p>
          </div>
        </div>

        <div className="bg-white rounded-xl p-6 space-y-4 border border-gray-200">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">Recipient Name</span>
              <p className="font-medium text-gray-900">{transferData.recipientName}</p>
            </div>
            <div>
              <span className="text-gray-500">Bank Name</span>
              <p className="font-medium text-gray-900">{transferData.bankName}</p>
            </div>
            <div>
              <span className="text-gray-500">SWIFT/BIC</span>
              <p className="font-medium text-gray-900">{transferData.swiftCode}</p>
            </div>
            <div>
              <span className="text-gray-500">Account Number</span>
              <p className="font-medium text-gray-900">{transferData.accountNumber}</p>
            </div>
            <div>
              <span className="text-gray-500">Country</span>
              <p className="font-medium text-gray-900">{transferData.country}</p>
            </div>
            <div>
              <span className="text-gray-500">Amount</span>
              <p className="font-medium text-gray-900">
                {formatCurrency(transferData.amount, transferData.currency)}
              </p>
            </div>
          </div>

          <div className="pt-4 flex space-x-4">
            <button
              type="button"
              onClick={() => setShowConfirmation(false)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              disabled={loading}
            >
              Back
            </button>
            <button
              type="button"
              onClick={handleConfirmTransfer}
              className="flex-1 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Confirm Transfer'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <form onSubmit={handleInitialSubmit} className="space-y-6">
      {error && (
          <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-red-700">{error}</p>
              </div>
            </div>
        </div>
      )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
          From Account
        </label>
        <select
          value={fromAccount}
          onChange={(e) => setFromAccount(e.target.value)}
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
          disabled={loading}
        >
          <option value="">Select account</option>
          {accounts.map((account) => (
            <option key={account.accountNumber} value={account.type}>
              {account.type} - {formatCurrency(account.available)}
            </option>
          ))}
        </select>
      </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
          Recipient Name
        </label>
        <input
          type="text"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          placeholder="Enter recipient's full name"
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
          disabled={loading}
        />
          </div>
      </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
          Bank Name
        </label>
        <input
          type="text"
          value={bankName}
          onChange={(e) => setBankName(e.target.value)}
          placeholder="Enter recipient's bank name"
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
          disabled={loading}
        />
      </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
          SWIFT/BIC Code
        </label>
        <input
          type="text"
          value={swiftCode}
          onChange={(e) => setSwiftCode(e.target.value.toUpperCase())}
          placeholder="Enter SWIFT/BIC code"
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
          maxLength="11"
          disabled={loading}
        />
          </div>
      </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
          Account Number
        </label>
        <input
          type="text"
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value.replace(/\s/g, ''))}
          placeholder="Enter recipient's account number"
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
          disabled={loading}
        />
      </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
          IBAN (Optional)
        </label>
        <input
          type="text"
          value={iban}
          onChange={(e) => setIban(e.target.value.toUpperCase())}
          placeholder="Enter IBAN (if applicable)"
              className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
          disabled={loading}
        />
          </div>
      </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
          Country
        </label>
        {renderCountryDropdown()}
      </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">
              Currency
            </label>
            {renderCurrencyDropdown()}
          </div>
        </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
            Amount
          </label>
          <div className="relative mt-1 rounded-xl shadow-sm">
            <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-4">
              <span className="text-gray-500 sm:text-sm">
                {currencies.find(c => c.code === currency)?.symbol || '$'}
              </span>
            </div>
          <input
            type="number"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
              placeholder="0.00"
              className="block w-full pl-8 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
                focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
                hover:bg-white transition-colors duration-200
                disabled:bg-gray-100 disabled:cursor-not-allowed
                text-sm"
            disabled={loading}
            min="0.01"
            step="0.01"
          />
        </div>
      </div>

        <div className="space-y-2">
          <label className="block text-sm font-medium text-gray-700">
          Reference
        </label>
        <input
          type="text"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
            placeholder="What's this transfer for?"
            className="block w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl 
              focus:ring-2 focus:ring-primary-500 focus:border-primary-500 
              hover:bg-white transition-colors duration-200
              disabled:bg-gray-100 disabled:cursor-not-allowed
              text-sm"
          disabled={loading}
        />
      </div>

        <div className="pt-4">
      <button
        type="submit"
        disabled={loading}
            className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl 
              shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 
              focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500
              disabled:bg-gray-300 disabled:cursor-not-allowed
              transition-colors duration-200"
          >
            {loading ? (
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            ) : (
              'Continue'
            )}
      </button>
        </div>
    </form>
    </div>
  );
} 